import {
  GeothermalDesignDocumentsSystemDetails,
  GeothermalEquipmentType,
  GeothermalIndoorUnitTypeDefaultEquipmentTypes,
  GeothermalSystem,
  GeothermalSystemDesign,
} from 'types';

export interface EquipmentFormValues {
  type: GeothermalEquipmentType;
  model: string;
  description: string;
}

export interface SystemDetailsFormValues {
  name: string;
  manufacturer: string;
  equipment: EquipmentFormValues[];
}

export interface DesignDocsFormValues {
  systems: SystemDetailsFormValues[];
}

/**
 * Correlates a system's stored equipment with the types its indoor unit requires, matching on
 * `type` and falling back to position for records written before `type` existed so a legacy
 * system's values aren't orphaned. Seeding the form and building the save payload both go through
 * this, so what is displayed and what is written cannot disagree.
 *
 * A system with no recognizable indoor unit type yields no rows. Assuming packaged would collapse a
 * split system's two entries into one and discard the other, which is what the server refuses to do.
 */
export const matchStoredEquipment = (system: GeothermalSystem) => {
  const requiredTypes = GeothermalIndoorUnitTypeDefaultEquipmentTypes[system.indoorUnitType] ?? [];
  const stored = system.equipment ?? [];
  const claimed = new Set<number>();

  return requiredTypes.map((type, typeIndex) => {
    let source = stored.findIndex((item, i) => !claimed.has(i) && item?.type === type);
    if (source === -1 && stored[typeIndex] && !stored[typeIndex].type && !claimed.has(typeIndex)) {
      source = typeIndex;
    }
    if (source !== -1) claimed.add(source);
    return { type, stored: source === -1 ? undefined : stored[source] };
  });
};

/** Seeds the form from the stored design, one row per equipment type the system requires. */
export const buildInitialValues = (storedSystems: GeothermalSystem[]): DesignDocsFormValues => ({
  systems: storedSystems.map((system, index) => ({
    name: system.name || `System ${index + 1}`,
    manufacturer: system.manufacturer || system.equipment?.[0]?.manufacturer || '',
    equipment: matchStoredEquipment(system).map(({ type, stored }) => ({
      type,
      model: stored?.model || '',
      description: stored?.description || '',
    })),
  })),
});

/** The compact submit payload. The server merges it onto the stored design. */
export const buildSystemDetails = (values: DesignDocsFormValues): GeothermalDesignDocumentsSystemDetails => ({
  systems: values.systems.map(({ equipment }) => ({
    equipment: equipment.map(({ type, model, description }) => ({ type, model, description })),
  })),
});

/**
 * The save payload. Saving reuses the system design create endpoint rather than a
 * design-documents-specific route, so every write to a geothermal design goes through the same
 * guards. That endpoint replaces the design wholesale, so everything not echoed back here is absent
 * from the new revision. Priced fields come straight off the stored design; only equipment changes.
 */
export const buildSystemDesignPayload = (
  storedDesign: GeothermalSystemDesign | undefined,
  values: DesignDocsFormValues,
) => {
  const storedSystems: GeothermalSystem[] = storedDesign?.systems ?? [];
  return {
    ...(storedDesign?.isPreliminary !== undefined ? { isPreliminary: storedDesign.isPreliminary } : {}),
    ...(storedDesign?.ecEligible !== undefined ? { ecEligible: storedDesign.ecEligible } : {}),
    systems: storedSystems.map((system, index) => {
      const enteredEquipment = values.systems[index]?.equipment ?? [];
      // A system whose indoor unit type is absent or unrecognized has no required types to rebuild
      // from, so rebuilding would write an empty array and this endpoint would replace the design
      // with it, dropping real equipment and any serial number captured at install. The server
      // declines to guess for the same reason; checking the map here keeps that guarantee off the
      // matcher, which exists to drive display.
      const isRecognizedIndoorUnitType = system.indoorUnitType in GeothermalIndoorUnitTypeDefaultEquipmentTypes;
      return {
        name: system.name,
        loopType: system.loopType,
        indoorUnitType: system.indoorUnitType,
        capacityTons: system.capacityTons,
        dhwAssist: system.dhwAssist,
        auxiliaryHeat: system.auxiliaryHeat,
        auxiliaryHeatType: system.auxiliaryHeatType,
        leaseAmount: system.leaseAmount,
        manufacturer: system.manufacturer,
        drillingCost: system.drillingCost,
        monitoringId: system.monitoringId,
        equipment: isRecognizedIndoorUnitType
          ? // oxlint-disable-next-line no-map-spread -- builds a new item; nothing is mutated
            matchStoredEquipment(system).map(({ type, stored }) => {
              const entered = enteredEquipment.find((item) => item.type === type);
              const model = entered?.model?.trim();
              const description = entered?.description?.trim();
              return {
                type,
                manufacturer: system.manufacturer,
                quantity: stored?.quantity ?? 1,
                ...(stored?.serialNumber ? { serialNumber: stored.serialNumber } : {}),
                ...(model ? { model } : {}),
                ...(description ? { description } : {}),
              };
            })
          : (system.equipment ?? []),
      };
    }),
  };
};

/** True when anything has been typed that would be lost by navigating away. */
export const hasEnteredSystemDetails = (values: DesignDocsFormValues) =>
  values.systems.some((system) =>
    system.equipment.some((item) => !!item.model?.trim() || !!item.description?.trim()),
  );

/** Every system has at least one equipment row and every row has a model. */
export const hasCompleteSystemDetails = (values: DesignDocsFormValues) =>
  values.systems.length > 0 &&
  values.systems.every(
    (system) => system.equipment.length > 0 && system.equipment.every((item) => !!item.model?.trim()),
  );
